import {
  Pressable,
  StyleSheet,
  Text,
  type PressableProps,
  type StyleProp,
  type ViewStyle,
} from "react-native";

import { webTapFloor, type UiTheme } from "../theme";

// THE shared chip / toggle-tag. Collapses the pill-and-box selection chips that
// were the app's biggest style silo + its worst "28/36/40/44/48 height chaos"
// (next/consolidation.md). One box, one height (36 + hitSlop 8 → ≥44 touch),
// one selected treatment. a11y: the selected state is NEVER colour-only — a
// selected chip renders a leading "✓ " and sets accessibilityState.selected, so
// the brand fill is a reinforcement, not the sole cue (Antenore 2026-07-02,
// matching the Settings pills). Migrating the fill chips to this FIXES ~6
// colour-only sites at once.
//
// Lean by design: this wave ships only what has 2+ real callsites — `shape`
// (pill toggles + box day/muscle chips), `dense` (the uppercase micro labels),
// `disabled`. The `tint` selection style, rich `leading`/`count` content, and
// the ChipStrip/RosterStrip migrations land in a later slice, with the props
// they need — not shipped speculatively here.

export type ChipProps = {
  theme: UiTheme;
  label: string;
  selected: boolean;
  onPress: () => void;
  /** Radius family. Pill (999) is the default toggle look; box (r3) for grids. */
  shape?: "pill" | "box";
  /** a11y role. Default "button"; pass "radio" inside a single-select
   *  radiogroup so a screen reader announces "radio, selected" instead of a
   *  bare button; pass "tab" inside a `tablist` when the chip SWITCHES which
   *  content panel is shown (navigation, not a form value) so the reader
   *  announces "tab, selected". `selected` still drives accessibilityState
   *  in every case. */
  role?: "button" | "radio" | "tab";
  /** Uppercase micro-label (xs/700) — the logger entry / set-type chips. */
  dense?: boolean;
  disabled?: boolean;
  /** Touch expansion. Defaults to 8 (→ ≥44). Pass `{top,bottom}` in tight rows. */
  hitSlop?: PressableProps["hitSlop"];
  /** Layout-only override (margins/flex/minWidth). Merged last. */
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  /** Extra spoken context (e.g. "Help mode is on — this explains instead of
   *  toggling"). Passthrough to the underlying Pressable; no visual effect. */
  accessibilityHint?: string;
  /** Set when the chip TOGGLES a fold rather than selecting something, so the
   *  reader announces collapsed/expanded. Added for EQ-1's machine chip, which
   *  replaced a hand-rolled disclosure whose `useDisclosure().a11yProps`
   *  already carried this — extending the primitive rather than forking it
   *  back out. Leave unset for ordinary chips. */
  expanded?: boolean;
  /**
   * Draw the outline at `borderStrong` (3.23:1) instead of `border` (~1.1:1
   * on `surface`).
   *
   * ⚠️ Opt-in ONLY because the default is a known defect, not because dim is
   * ever right: `DS-37` is exactly "the Chip's border does not exist", and
   * `IconButton` already made this move under DS-9/DS-23. Use it on a chip
   * that is a CONTROL the eye has to find — EQ-1's gym chip sits alone in a
   * header and reads as floating text without it.
   *
   * ⛔ When DS-37 fixes the default app-wide this prop becomes a no-op and
   * should be deleted, not left as a second way to say the same thing.
   */
  prominent?: boolean;
};

export function Chip({
  theme,
  label,
  selected,
  onPress,
  shape = "pill",
  role = "button",
  dense = false,
  disabled = false,
  hitSlop = 8,
  style,
  accessibilityLabel,
  accessibilityHint,
  expanded,
  prominent = false,
}: ChipProps) {
  const styles = makeStyles(theme);

  return (
    <Pressable
      accessibilityRole={role}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityHint={accessibilityHint}
      accessibilityState={{
        selected,
        disabled,
        ...(expanded === undefined ? {} : { expanded }),
      }}
      // ⛔ And the SAME thing again as an aria prop, because on
      // react-native-web `accessibilityState.expanded` never reaches the DOM:
      // measured 2026-08-19 by rendering this chip headless and reading the
      // element back — `aria-label` and `role="button"` were there,
      // `aria-expanded` was absent both collapsed and expanded. So every chip
      // disclosure was silent to a screen reader on web while working fine on
      // native — the same shape as DS-48 (RNW's Pressable ignoring `hitSlop`),
      // and just as invisible to a typecheck or a test.
      // RN maps `aria-expanded` natively too, so this is one prop, both hosts.
      {...(expanded === undefined ? {} : { "aria-expanded": expanded })}
      disabled={disabled}
      onPress={onPress}
      hitSlop={hitSlop}
      style={({ pressed }) => [
        styles.base,
        shape === "pill" ? styles.pill : styles.box,
        dense && styles.dense,
        prominent && styles.prominent,
        selected && styles.selected,
        pressed && !disabled && styles.pressed,
        disabled && styles.disabled,
        style,
      ]}
    >
      <Text
        style={[
          styles.label,
          dense && styles.labelDense,
          selected && styles.labelSelected,
        ]}
        numberOfLines={1}
      >
        {/* Non-colour selected cue (a11y): the check pairs with the fill. */}
        {selected ? "✓ " : ""}
        {label}
      </Text>
    </Pressable>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    base: {
      // DS-51 — 36 is the visual size and it stays 36 (DESIGN.md: compact
      // controls, the target is reached by hitSlop, never by inflating the
      // box). But react-native-web's Pressable ignores `hitSlop` entirely, so
      // on web that floor never arrived; `webTapFloor` is `undefined` on
      // native, which leaves the shipped mobile look untouched.
      minHeight: webTapFloor ?? 36,
      paddingHorizontal: s.s4,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      alignItems: "center",
      justifyContent: "center",
    },
    pill: { borderRadius: 999 },
    box: { borderRadius: r.r3 },
    dense: { paddingHorizontal: s.s2, minWidth: webTapFloor ?? 36 },
    // Same move IconButton made under DS-9/DS-23: `border` on `surface` is
    // ~1.1:1 and simply is not there, `borderStrong` is 3.23:1.
    prominent: { borderColor: p.borderStrong },
    selected: { backgroundColor: p.brand500, borderColor: p.brand500 },
    pressed: { opacity: 0.7 },
    disabled: { opacity: 0.5 },

    label: { color: p.ink2, fontSize: t.sizes.sm, fontWeight: "600" },
    labelDense: {
      fontSize: t.sizes.xs,
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.4,
    },
    labelSelected: { color: p.inkOnBrand },
  });
}
